import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Check, ArrowRight, AlertCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CodyIconBadge } from '../components/CodyLogo';
import api from '../lib/api';
import redditPixel from '../lib/tracking';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | failed
  const [plan, setPlan] = useState('');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }

    const poll = async () => {
      try {
        const data = await api.request(`/api/stripe/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          setPlan(data.plan);
          redditPixel.purchaseCompleted(data.plan, 0, sessionId);
          return true;
        }
        if (data.status === 'expired') {
          setStatus('failed');
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

    let count = 0;
    const interval = setInterval(async () => {
      count++;
      setAttempts(count);
      const done = await poll();
      if (done || count >= 10) clearInterval(interval);
      if (count >= 10 && status === 'checking') setStatus('failed');
    }, 2000);

    poll(); // immediate first check

    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-4">
      <div className="absolute top-0 left-0 right-0 h-[40vh] hero-gradient" />
      <Card className="relative w-full max-w-md p-8 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border-[#E6E8EE]">
        <div className="flex items-center justify-center mb-6"><CodyIconBadge size={44} /></div>

        {status === 'checking' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
            <Loader2 className="w-10 h-10 text-[#FF4500] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Confirming your payment</h2>
            <p className="text-sm text-[#5B667A]">This usually takes a few seconds...</p>
          </motion.div>
        )}

        {status === 'paid' && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-[#E9F9EF] flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#16A34A]" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk'" }}>Welcome to {plan === 'agency' ? 'Agency' : 'Growth'}!</h2>
            <p className="text-sm text-[#5B667A] mb-6">Your plan has been upgraded. All premium features are now unlocked.</p>
            <Button className="h-11 px-8 rounded-xl font-bold text-sm cta-btn shadow-lg shadow-[#FF4500]/20"
              style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
              onClick={() => { window.location.href = '/dashboard'; }}>
              Go to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>
        )}

        {status === 'failed' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk'" }}>Payment not confirmed</h2>
            <p className="text-sm text-[#5B667A] mb-6">Something went wrong. If you were charged, please contact support.</p>
            <Button variant="outline" onClick={() => navigate('/settings')}>Back to Settings</Button>
          </motion.div>
        )}
      </Card>
    </div>
  );
}
